import React, { useCallback, useEffect, useState } from 'react';
import { Box, Typography, Button, Tooltip } from '@mui/material';
import GavelOutlinedIcon from '@material-ui/icons/GavelOutlined';
import TrendingUpOutlinedIcon from '@material-ui/icons/TrendingUpOutlined';
import FormatListNumberedOutlinedIcon from '@material-ui/icons/FormatListNumberedOutlined';
import { getPartnerRequestOptionsService } from '../../../services/client.service';
import { useAuth } from '../../../context/auth';
import UnlockCapabilityDialog from '../DQProV2/components/UnlockCapabilityDialog.comp';

/**
 * Solutions > COS Government Checklist > Overview — Common Section screen,
 * same family as the other pages in this folder (partner-only; the parent
 * Solutions Overview already hides this whole card from non-owners).
 *
 * Content and structure here are reproduced exactly from the reference
 * prototype (~/Downloads/Solutions (standalone).html, its `isGov` render
 * block — the "What it covers" / "What it helps governments do" / "How it
 * works" sections, the 85-indicator stat, the six categories, the five
 * steps, and the restricted-access notice, all verbatim) per explicit
 * instruction that nothing about this page's content should change from the
 * mockup. Colors/shapes still follow this app's own tokens (orange, not the
 * mockup's own teal), same as every other Solutions page in this folder.
 *
 * "Request Access" (Button Registry BTN-0018) opens the "Unlock a capability"
 * dialog with the COS Government Checklist already picked (PW deck slide 9:
 * "check and continue: Popup + System Email") — the same partner request as
 * any other row there, decided by a DQ administrator in the admin portal.
 * Once asked, the button reads "Access requested" until DQ decides.
 */

const BORDER = 'var(--brand-border, #E5E7EB)';
const MUTED = 'var(--brand-text-muted, #6B7280)';
const TEXT = 'var(--brand-text, #111827)';
const ORANGE = '#FF6900';

const GOV_CATEGORIES = [
    'Laws & governance',
    'Families & communities',
    'Education',
    'Industry accountability',
    'Technology infrastructure',
    'Public services',
];

const WHAT_IT_HELPS_DO = [
    { title: 'Assess National Readiness', desc: 'Build a standardized view of current system capability.' },
    { title: 'Validate Evidence', desc: 'Review the evidence supporting each indicator and proposed score.' },
    { title: 'Identify Gaps', desc: 'See where policies, implementation, infrastructure, or services require strengthening.' },
    { title: 'Prioritize Action', desc: 'Focus national efforts on the gaps with the greatest potential impact.' },
];

const HOW_IT_WORKS = [
    { no: '1', title: 'Review', desc: 'Review all indicators and underlying assessment items.' },
    { no: '2', title: 'Confirm', desc: 'Agree with the current assessment or propose a revision.' },
    { no: '3', title: 'Explain', desc: 'Provide a structured rationale for any proposed change.' },
    { no: '4', title: 'Evidence', desc: 'Attach traceable supporting evidence.' },
    { no: '5', title: 'Submit', desc: 'Submit the completed review for validation.' },
];

const plainCardSx = {
    border: `1px solid ${BORDER}`,
    borderRadius: '14px',
    boxShadow: '0px 2px 8px rgba(0,0,0,0.04)',
    backgroundColor: 'var(--brand-surface, #ffffff)',
};

const eyebrowSx = {
    fontSize: '11px', fontWeight: 700, letterSpacing: '0.6px',
    textTransform: 'uppercase', color: MUTED,
};

const cardHeaderIconSx = {
    width: 36, height: 36, borderRadius: '10px',
    backgroundColor: 'rgba(255, 105, 0, 0.08)',
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    flexShrink: 0,
};

const CardHeader = ({ icon: Icon, title }) => (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, pb: 2, mb: 2.5, borderBottom: `1px solid ${BORDER}` }}>
        <Box sx={cardHeaderIconSx}>
            <Icon sx={{ fontSize: 20, color: ORANGE }} />
        </Box>
        <Typography sx={{ fontSize: '17px', fontWeight: 800, color: TEXT }}>
            {title}
        </Typography>
    </Box>
);

/** The partner request type this page raises (PARTNER_REQUEST_TYPE). */
const REQUEST_TYPE = 'government_checklist';

const shortDate = (at) => {
    const d = at ? new Date(at) : null;
    if (!d || Number.isNaN(d.getTime())) return '';
    return d.toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' });
};

const primaryBtnSx = {
    backgroundColor: ORANGE, color: '#fff', textTransform: 'none',
    fontWeight: 700, fontSize: '14px', borderRadius: '50px', px: 2.75, py: 1.25,
    whiteSpace: 'nowrap',
    '&:hover': { backgroundColor: '#e55e00' },
    '&.Mui-disabled': { backgroundColor: BORDER, color: MUTED },
};

const SolutionGovernmentChecklistOverviewPage = () => {
    // GET partner-request/options; null until loaded.
    const [options, setOptions] = useState(null);
    const [dialogOpen, setDialogOpen] = useState(false);
    const { authUser } = useAuth();

    const loadOptions = useCallback(async () => {
        try {
            const res = await getPartnerRequestOptionsService();
            setOptions(res?.data || { can_request: true, types: [] });
        } catch (e) {
            // invokeApi toasts; the button stays usable and the server refuses a duplicate.
            setOptions({ can_request: true, types: [] });
        }
    }, []);

    useEffect(() => { loadOptions(); }, [loadOptions]);

    const type = (options?.types || []).find((t) => t.key === REQUEST_TYPE) || null;

    // Closing the dialog may follow a request just sent — refresh the button.
    const handleDialogClose = () => {
        setDialogOpen(false);
        loadOptions();
    };

    const canRequest = options?.can_request !== false;
    const pending = type?.state === 'requested';
    const approved = type?.state === 'approved';
    const buttonLabel = pending ? 'Access requested' : approved ? 'Access approved' : 'Request Access';
    const buttonHint = !canRequest
        ? 'Only the workspace owner can request access.'
        : pending
        ? `Requested on ${shortDate(type.requested_at)} — the DQ team is reviewing it.`
        : '';

    return (
    <Box sx={{ width: '100%', maxWidth: '100%', px: { xs: 2, sm: 0 }, py: { xs: 3, sm: 5 } }}>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3, mb: 5 }}>
            <Box>
                <Typography sx={{ fontSize: { xs: '24px', sm: '30px' }, fontWeight: 800, color: TEXT, mb: 1.5 }}>
                    Strengthen National Child Online Protection Systems
                </Typography>
                <Typography sx={{ fontSize: '15px', color: MUTED, lineHeight: 1.6 }}>
                    The COS Government Checklist helps governments review national child online protection capacity,
                    validate evidence, identify implementation gaps, and prioritize action.
                </Typography>
            </Box>

            {/* Side panel — full width, row layout instead of a narrow stacked card. */}
            <Box sx={{
                ...plainCardSx, p: 3, width: '100%',
                display: 'flex', flexDirection: { xs: 'column', sm: 'row' },
                justifyContent: 'space-between', alignItems: { xs: 'stretch', sm: 'center' }, gap: 2,
            }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: { xs: 1, sm: 3 }, flexWrap: 'wrap' }}>
                    <Typography sx={{ fontSize: '20px', fontWeight: 800, color: TEXT }}>
                        COS Government Checklist
                    </Typography>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Box sx={{ width: 8, height: 8, borderRadius: '50%', backgroundColor: '#D4A72C', flexShrink: 0 }} />
                        <Typography sx={{ fontSize: '14px', color: MUTED }}>Restricted access</Typography>
                    </Box>
                </Box>
                <Tooltip title={buttonHint} arrow>
                    <span style={{ flexShrink: 0 }}>
                        <Button
                            disabled={!type || !canRequest || pending || approved}
                            onClick={() => setDialogOpen(true)}
                            sx={primaryBtnSx}
                        >
                            {buttonLabel}
                        </Button>
                    </span>
                </Tooltip>
            </Box>
        </Box>

        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: 'repeat(3, 1fr)' }, gap: 3, mb: 5, alignItems: 'stretch' }}>
                    {/* Card 1 — What it covers */}
                    <Box sx={{ ...plainCardSx, p: { xs: 3, sm: 3.5 }, display: 'flex', flexDirection: 'column' }}>
                        <CardHeader icon={GavelOutlinedIcon} title="What it covers" />
                        <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 1.25, mb: 1.5 }}>
                            <Typography sx={{ fontSize: '32px', fontWeight: 800, color: TEXT, lineHeight: 1 }}>85</Typography>
                            <Typography sx={{ fontSize: '15px', fontWeight: 800, color: TEXT }}>system indicators</Typography>
                        </Box>
                        <Typography sx={{ fontSize: '13.5px', color: MUTED, lineHeight: 1.7, mb: 2.5 }}>
                            The 85 system indicators complement 15 children indicators developed from the COS Children
                            Assessment. Together they form the basis of the CPC Index.
                        </Typography>
                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.25, mb: 2.5 }}>
                            {GOV_CATEGORIES.map((cat) => (
                                <Box key={cat} sx={{ display: 'flex', gap: 1.25, alignItems: 'flex-start' }}>
                                    <Box sx={{
                                        width: 6, height: 6, borderRadius: '50%', backgroundColor: ORANGE,
                                        flexShrink: 0, mt: '7px',
                                    }} />
                                    <Typography sx={{ fontSize: '13.5px', color: MUTED, lineHeight: 1.7 }}>
                                        {cat}
                                    </Typography>
                                </Box>
                            ))}
                        </Box>
                        <Box sx={{ mt: 'auto' }}>
                            <Typography sx={{ ...eyebrowSx, mb: 0.5 }}>Used for</Typography>
                            <Typography sx={{ fontSize: '13.5px', color: MUTED, lineHeight: 1.7 }}>
                                The CPC Index, the global benchmark for child online protection.
                            </Typography>
                        </Box>
                    </Box>

                    {/* Card 2 — What it helps governments do */}
                    <Box sx={{ ...plainCardSx, p: { xs: 3, sm: 3.5 }, display: 'flex', flexDirection: 'column' }}>
                        <CardHeader icon={TrendingUpOutlinedIcon} title="What it helps governments do" />
                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5 }}>
                            {WHAT_IT_HELPS_DO.map((item) => (
                                <Box key={item.title}>
                                    <Typography sx={{ fontSize: '15px', fontWeight: 800, color: TEXT, mb: 0.5 }}>
                                        {item.title}
                                    </Typography>
                                    <Typography sx={{ fontSize: '13.5px', color: MUTED, lineHeight: 1.7 }}>
                                        {item.desc}
                                    </Typography>
                                </Box>
                            ))}
                        </Box>
                    </Box>

                    {/* Card 3 — How it works */}
                    <Box sx={{ ...plainCardSx, p: { xs: 3, sm: 3.5 }, display: 'flex', flexDirection: 'column' }}>
                        <CardHeader icon={FormatListNumberedOutlinedIcon} title="How it works" />
                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5 }}>
                            {HOW_IT_WORKS.map((step) => (
                                <Box key={step.no} sx={{ display: 'flex', gap: 1.5, alignItems: 'baseline' }}>
                                    <Typography sx={{ fontSize: '13px', fontWeight: 700, color: ORANGE, flexShrink: 0, width: '16px', textAlign: 'right' }}>
                                        {step.no}
                                    </Typography>
                                    <Box>
                                        <Typography sx={{ fontSize: '15px', fontWeight: 800, color: TEXT, mb: 0.5 }}>
                                            {step.title}
                                        </Typography>
                                        <Typography sx={{ fontSize: '13.5px', color: MUTED, lineHeight: 1.7 }}>
                                            {step.desc}
                                        </Typography>
                                    </Box>
                                </Box>
                            ))}
                        </Box>
                    </Box>
                </Box>

        {/* Restricted access */}
        <Box sx={{
            backgroundColor: 'var(--brand-bg, #FCFCFA)', border: `1px dashed ${BORDER}`,
            borderRadius: '14px', px: 3, py: 2.75,
        }}>
            <Typography sx={{ ...eyebrowSx, mb: 0.75 }}>Restricted access</Typography>
            <Typography sx={{ fontSize: '13.5px', color: MUTED, lineHeight: 1.7 }}>
                Available to national governments and government-designated entities.
            </Typography>
        </Box>

        <UnlockCapabilityDialog
            open={dialogOpen}
            onClose={handleDialogClose}
            authUser={authUser}
            initialSelected={REQUEST_TYPE}
            source="solution_overview"
        />
    </Box>
    );
};

export default SolutionGovernmentChecklistOverviewPage;
